/**
 * LegendRenderer - Универсальный компонент для рендеринга легенд любого типа
 *
 * Поддерживает:
 * - Typed легенды (colored-items, gradient, size-gradient, size-range-horizontal, terrain) - автоматический рендеринг
 * - Custom легенды - используют кастомный компонент/функцию рендеринга
 *
 * Пример использования:
 *
 * // Простая типизированная легенда
 * <LegendRenderer config={LEGENDS.POI} />
 *
 * // Кастомная легенда (например, Aspect)
 * <LegendRenderer config={LEGENDS.ASPECT} />
 */

import React from 'react';
import PropTypes from 'prop-types';
import { i18n } from '@lingui/core';
import styles from '../FilterSidebar/FilterSidebar.module.css';
import { LEGEND_TYPES, LEGEND_RENDER_CONFIG_DEFAULTS } from '../../types/legendTypes';
import { ColoredItemsLegend } from './types/ColoredItemsLegend';
import { GradientLegend } from './types/GradientLegend';
import { DynamicGradientLegend } from './types/DynamicGradientLegend';
import { SizeGradientLegend } from './types/SizeGradientLegend';
import { SizeRangeHorizontalLegend } from './types/SizeRangeHorizontalLegend';
import { H3DynamicLegend } from './types/H3DynamicLegend';
import { getStyleLegendItems } from '../../constants/legendStylesResolver';

export const LegendRenderer = ({ config, className = '' }) => {
    if (!config) {
        return null;
    }

    const {
        type,
        title,
        items: configItems = [],
        styleKey,
        style,
        icon,
        isDynamic = false,
        dynamicConfig = {},
        renderConfig = {},
        customRenderer: CustomComponent,
    } = config;

    // Если есть styleKey и style, подтягиваем items из стиля
    // Иначе используем items из конфига
    let items = configItems;
    if (styleKey && style) {
        items = getStyleLegendItems(style, styleKey);
    }

    const finalRenderConfig = {
        ...LEGEND_RENDER_CONFIG_DEFAULTS[type],
        ...renderConfig,
    };
    const headerClass = `${styles.legHead} ${className}`;

    // Кастомные легенды используют свой renderer
    if (type === LEGEND_TYPES.CUSTOM && CustomComponent) {
        return (
            <div style={{ maxWidth: '300px' }}>
                {title && <div className={headerClass}>{i18n._(title)}</div>}
                <CustomComponent config={config} />
            </div>
        );
    }

    // H3 динамическая легенда
    if (type === LEGEND_TYPES.H3_DYNAMIC) {
        // Используем H3DynamicLegend (для агрегированных треков)
        return (
            <div style={{ maxWidth: '300px' }}>
                {title && <div className={headerClass}>{i18n._(title)}</div>}
                <H3DynamicLegend renderConfig={finalRenderConfig} />
            </div>
        );
    }

    // Типизированные легенды
    return (
        <div style={{ maxWidth: '300px' }}>
            {title && <div className={headerClass}>{i18n._(title)}</div>}

            {type === LEGEND_TYPES.COLORED_ITEMS && (
                <ColoredItemsLegend
                    items={items}
                    icon={icon}
                    renderConfig={finalRenderConfig}
                />
            )}

            {type === LEGEND_TYPES.GRADIENT && isDynamic && (
                <DynamicGradientLegend
                    items={items}
                    renderConfig={finalRenderConfig}
                    h3ZoomReduxKey={dynamicConfig.h3ZoomReduxKey}
                    baseZoom={dynamicConfig.baseZoom}
                    scalingFactor={dynamicConfig.scalingFactor}
                    minZoom={dynamicConfig.minZoom}
                    maxZoom={dynamicConfig.maxZoom}
                    baseValues={dynamicConfig.baseValues}
                />
            )}

            {type === LEGEND_TYPES.GRADIENT && !isDynamic && (
                <GradientLegend
                    items={items}
                    renderConfig={finalRenderConfig}
                />
            )}

            {type === LEGEND_TYPES.SIZE_GRADIENT && (
                <SizeGradientLegend
                    items={items}
                    renderConfig={finalRenderConfig}
                />
            )}

            {type === LEGEND_TYPES.SIZE_RANGE_HORIZONTAL && (
                <SizeRangeHorizontalLegend
                    items={items}
                    renderConfig={finalRenderConfig}
                />
            )}

            {type === LEGEND_TYPES.TERRAIN && (
                <div className={finalRenderConfig.containerClass}>
                    {/* Terrain легенды должны быть обработаны их кастомными компонентами */}
                    Unsupported: Use CustomComponent for terrain legends
                </div>
            )}
        </div>
    );
};

LegendRenderer.propTypes = {
    config: PropTypes.shape({
        type: PropTypes.string.isRequired,
        title: PropTypes.object,
        items: PropTypes.array,
        styleKey: PropTypes.string,  // Ключ в стиле для автоматического подтягивания items
        style: PropTypes.object,     // Объект стиля слоя (например, HOUSES_STYLE)
        icon: PropTypes.string,
        isDynamic: PropTypes.bool,
        dynamicConfig: PropTypes.shape({
            h3ZoomReduxKey: PropTypes.string,
            baseZoom: PropTypes.number,
            scalingFactor: PropTypes.number,
            minZoom: PropTypes.number,
            maxZoom: PropTypes.number,
            baseValues: PropTypes.arrayOf(PropTypes.number),
        }),
        renderConfig: PropTypes.object,
        customRenderer: PropTypes.oneOfType([ PropTypes.func, PropTypes.object ]),
    }),
    className: PropTypes.string,
};

export default LegendRenderer;
